import moment from "moment-timezone";
import template from "./year-month.html";
import "./year-month.less";
import {
	find,
	orderBy
} from "lodash";
import {
	EVENT_FILTER_PRIMARYDATE,
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_SET_VALUE,
	EVENT_REVERT_VALUES,
	EVENT_VALUE_CHANGED,
	EVENT_PAGE_LOADED,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import { isValueNullish } from "../../../../utils/valueUtilities";

const months = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December"
];

export class Controller {
	selection = {};

	constructor($scope, eventBus, $timeout, $logger, appState, uiHelpers) {
		this._scope = $scope;
		this.$timeout = $timeout;
		this.$logger = $logger;
		this.appState = appState;
		this.uiHelpers = uiHelpers;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		const form = this.appState.forms.selected;

		this.availableYears = [];
		let earliestYear = orderBy(
			form.Seasons.map((season) => {
				return moment(season.BeginInclusive).year();
			}),
			(year) => year,
			["asc"]
		)[0];
		const currentYear = moment().year();

		while(earliestYear <= currentYear) {
			this.availableYears.push(earliestYear);
			earliestYear++;
		}

		this.availableYears = orderBy(this.availableYears, (year) => year, ["desc"]);
		this.allOptions = {
			months: months.map((c, index) => ({ Name: c, Code: c, Order: index })),
			years: this.availableYears.map((c, index) => ({ Name: c.toString(), Code: c.toString(), Order: index }))
		};

		this._unregister = this.reportFormCtrl.registerFieldController(this);

		this.setValue();

		this.monthLabel = `${this.field.Name}-month`;
		this.yearLabel = `${this.field.Name}-year`;
		this.monthFieldId = `${this.field.Id}-month`;
		this.yearFieldId = `${this.field.Id}-year`;

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (e, params) => {
			if(!this.cleared && (!!this.selectedMonth) && params.isInitializing !== true) {
				this.cleared = {
					selectedMonth: {
						month: this.month,
						year: this.year
					}
				};
			}
			this.selectedMonth = null;
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!this.cleared) {
				this.selectedMonth = this.cleared.selectedMonth;
				this.cleared = undefined;
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => {
			if(!this.selectedMonth) {
				this.externalSet = true;
				this.selectedMonth = {
					month: moment(params.value).month(),
					year: moment(params.value).year()
				};

				this.$timeout(() => this._scope.$apply());
			}
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_PAGE_LOADED, () => {
			this.initialValue = find(this.values, v => v.FieldId === this.field.Id);
			if(!!this.initialValue) {
				const localShiftedMoment = moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");
				this.initialValue.Date = moment(localShiftedMoment.toDate()).tz(this.field.timezone, true)
					.format("YYYY-MM-DDTHH:mm:00Z");
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	getCurrentValue(isApiCall = false) {
		let result = this.buildResult();

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}
			result.isDirty = (result.Date && moment(result.Date).format()) != (this.initialValue && moment(this.initialValue.Date).format());
		}

		return result ? [result] : [];
	}

	convertJsDateToYearMonth(date) {
		if(!date) {
			return undefined;
		}

		return {
			month: this.allOptions.months[moment(date).month()].Code,
			year: moment(date)
				.year()
				.toString()
		};
	}

	revertValue() {
		if(this.initialValue) {
			const localShiftedMoment = this.initialValue && moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");

			const date = localShiftedMoment.toDate(); //this value is now clean for the field

			this.selectedMonth = this.convertJsDateToYearMonth(date);
		}
		else {
			this.selectedMonth = undefined;
		}
	}

	setPristine() {
		this.initialValue = this.buildResult();
	}

	getJsDate = () => {
		if(!this.field.timezone) {
			return null;
		}

		if(isValueNullish(this.selectedMonth)) {
			return null;
		}

		const outgoingDate = moment.utc()
			.year(this.selectedMonth.year)
			.month(this.selectedMonth.month)
			.date(1)
			.hours(0)
			.minutes(0)
			.seconds(0)
			.milliseconds(0)
			.tz(this.field.timezone, true);

		return outgoingDate;
	}

	buildResult() {
		if(!this.field.timezone) {
			return null;
		}

		if(this.selectedMonth !== undefined) {
			const outgoingDate = this.getJsDate();

			if(outgoingDate !== null) {
				return {
					FieldId: this.field.Id,
					Date: outgoingDate.format()
				};
			}
		}

		return undefined;
	}

	setValue() {
		this.value = find(this.values, v => v.FieldId === this.field.Id);

		if(this.value) {
			// This is incoming data path from VESL server/API
			const localShiftedMoment = moment(this.value.Date, "YYYY-MM-DDThh:mm:ss");

			const date = localShiftedMoment.toDate(); //this value is now clean for the field

			this.selectedMonth = this.convertJsDateToYearMonth(date);
		}
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	setMonthDisplay(id) {
		const item = this.allOptions.months.find(o => o.Code === id);

		if(!item) {
			throw new Error(`Selected ${this.field.Name} not found as a month option.`);
		}

		const entityDisplay = this.uiHelpers.getEntityDisplay(item, undefined, true, true);

		return entityDisplay;
	}

	setYearDisplay(id) {
		const item = this.allOptions.years.find(o => o.Code === id);

		if(!item) {
			throw new Error(`Selected ${this.field.Name} not found as a year option.`);
		}

		const entityDisplay = this.uiHelpers.getEntityDisplay(item, undefined, true, true);

		return entityDisplay;
	}

	handleMonthChanged(modelVal) {
		if(modelVal !== null && modelVal !== undefined) {
			this.selectedMonth = {
				...this.selectedMonth ?? {},
				month: modelVal
			};
			this.initialValueSet = false;
		}

		this.cleared = undefined;
		if(!!this.isMonthReady && this.selectedMonth?.year) {
			this.triggerValueChanged(this.getJsDate());
		}
	}

	handleYearChanged(modelVal) {
		if(modelVal !== null && modelVal !== undefined) {
			this.selectedMonth = {
				...this.selectedMonth ?? {},
				year: modelVal
			};
			this.initialValueSet = false;
		}

		this.cleared = undefined;
		if(!!this.isYearReady && this.selectedMonth?.month) {
			this.triggerValueChanged(this.getJsDate());
		}
	}

	triggerValueChanged(value) {
		const params = {
			field: this.field,
			value
		};

		if(this.initialValueSet) {
			params.initialValue = true;
		}

		if(this.field.IsPrimary) {
			const asOf = moment(value).tz(this.field.timezone, true);
			this._eventBus.trigger(EVENT_FILTER_PRIMARYDATE, {
				effectiveAsOf: {
					fromInclusive: asOf.clone()
						.startOf("month"),
					toExclusive: asOf.clone().add(1, "month")
						.startOf("month"), allowPartial: true
				},
				autoSelect: true
			});
		}
		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), params);

		this.updateFieldDirty();
	}

	updateFieldDirty() {
		const result = this.getCurrentValue();
		const isDirty = (this.field.visible !== false || this.initialValue !== undefined) && result[0].isDirty;

		this.setFieldDirty(this.field.Id, isDirty);
	}

	setMonthBox(box) {
		this.monthBox = box;
		const months = this.allOptions.months;
		const monthChoices = this.uiHelpers.setDisplay(
			months,
			{
				recentlyUsedEnabled: false,
				hideCode: true
			}
		);
		this.monthBox.setChoices(monthChoices, "value", "label");

		if(this.selectedMonth?.month) {
			this.monthBox.setValueByChoice(this.selectedMonth?.month);
		}

		this.isMonthReady = true;
	}

	setYearBox(box) {
		this.yearBox = box;
		const years = this.allOptions.years;
		const yearChoices = this.uiHelpers.setDisplay(
			years,
			{
				recentlyUsedEnabled: false,
				hideCode: true
			}
		);
		this.yearBox.setChoices(yearChoices, "value", "label");

		if(this.selectedMonth?.year) {
			this.yearBox.setValueByChoice(this.selectedMonth?.year);
		}

		this.isYearReady = true;
	}
}

export default angular
	.module("vesl.components.report-form.year-month", [])
	.component("yearMonth", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
